import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { brandPropTypes } from "app/utils/propTypes";
import PropTypes from "prop-types";
import "./Listing.scss";
import "src/app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchForm.scss";
import ListingHeaderContainer from "app/pages/Listing/ListingHeader/ListingHeaderContainer";
import { BRANDS, PRODUCT_EXPERIENCES } from "app/constants";
import { AsideProductFiltersContainer } from "app/pages/.shared/AsidePanel/AsideProductFilters/AsideProductFiltersContainer";
import { AsideProductFiltersMenu } from "@brand/AsidePanel/AsideProductFilters/AsideProductFiltersMenu";
import ProductsFilterContainer from "@brand/ProductsFilter/ProductsFilterContainer";
import { scrollToElement } from "app/utils/scroller";
import { forceCheck } from "react-lazyload";
import CookiePolicyFooterContainer from "app/pages/.shared/CookiePolicyFooter/CookiePolicyFooterContainer";
import ListingContentContainer from "app/pages/Listing/ListingContent/ListingContentContainer";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import PartnerSEO from "app/pages/.shared/PartnerSEO/PartnerSEO";
import ProductsFilter from "@brand/ProductsFilter/ProductsFilter";
import useOnSticky from "app/pages/.shared/useOnSticky";
import ListingHeadMetadata from "app/pages/Listing/ListingHeadMetadata";
import ScrollToTopButton from "app/pages/.shared/ScrollToTopButton/ScrollToTopButton";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { useIntl } from "react-intl";
import {
	FilterProvider,
	FilterTrigger,
	FilterDrawer,
	StickyFilterButtonTrigger,
	FILTER_SELECTION_TYPES,
	OPTION_MODE,
} from "app/features/product/productFilters";

const Listing = ({
	brand,
	idSpotlight,
	displayProductsCount,
	listingSEO = {},
	resolution,
	products,
}) => {
	const listingHeaderRef = useRef();
	const { ref, isSticky } = useOnSticky();
	const { formatDate, formatMessage } = useIntl();
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const asideProductFiltersMenuComponent = useCallback(
		props => {
			return (
				(brand === BRANDS.AF || brand === BRANDS.TO || brand === BRANDS.UG) && (
					<AsideProductFiltersMenu
						scrollToListingHeader={scrollToListingHeader}
						{...props}
					/>
				)
			);
		},
		[brand]
	);

	useLayoutEffect(() => {
		// force l'affichage des images des produits sur mobile lorsqu'un filtre est actif
		forceCheck();
	}, []);

	const scrollToListingHeader = () => {
		scrollToElement({
			element: listingHeaderRef.current,
			offset: 0,
			disableSmoothScroll: true,
		});
	};

	const shouldShowMultiStageJourneyOption = products?.some(product =>
		[
			PRODUCT_EXPERIENCES.CIRCUIT,
			PRODUCT_EXPERIENCES.AUTOTOUR,
			PRODUCT_EXPERIENCES.CRUISE,
		].includes(product?.productExperience)
	);

	const facetConfigs = useMemo(
		() => [
			{
				field: "destinations",
				title: formatMessage({ id: "product.page.filter.destinations.label" }),
				valueGetter: item => item.country,
				selectionMode: FILTER_SELECTION_TYPES.SINGLE,
				...(shouldShowMultiStageJourneyOption && {
					buildOptions: () => {
						return [
							{
								value: "multi-stage-journey",
								label: formatMessage({
									id: "listing.filter.destination.multi.destinations.label",
								}),
							},
						];
					},
					optionMode: OPTION_MODE.MERGE,
					customMatcher: product => {
						return [
							PRODUCT_EXPERIENCES.CIRCUIT,
							PRODUCT_EXPERIENCES.AUTOTOUR,
							PRODUCT_EXPERIENCES.CRUISE,
						].includes(product?.productExperience);
					},
				}),
			},
			{
				field: "themes",
				title: formatMessage({ id: "listing.filter.theme.label" }),
				valueGetter: item => item.topics,
				selectionMode: FILTER_SELECTION_TYPES.SINGLE,
			},
			{
				field: "months",
				title: formatMessage({ id: "listing.filter.month.label" }),
				valueGetter: item => item.months.map(month => month.label),
				formatLabel: ({ label }) => {
					const [year, month] = label.split("/");
					const date = new Date(Number(year), Number(month) - 1, 1);
					return formatDate(date, { month: "long", year: "numeric" });
				},
				selectionMode: FILTER_SELECTION_TYPES.SINGLE,
			},
		],
		[shouldShowMultiStageJourneyOption]
	);

	return (
		<FilterProvider facetConfigs={facetConfigs} products={products}>
			<AsideProductFiltersContainer>
				<ProductsFilterContainer
					asideFilterMenu={false}
					renderComponent={asideProductFiltersMenuComponent}
				/>
			</AsideProductFiltersContainer>

			<div className="listing">
				<ListingHeadMetadata />
				<ListingHeaderContainer
					idSpotlight={idSpotlight}
					scrollToListingHeader={scrollToListingHeader}
					listingHeaderRef={listingHeaderRef}
					displayProductsCount={displayProductsCount}
				/>

				{isMobile ? (
					<>
						<FilterTrigger asChild>
							<StickyFilterButtonTrigger />
						</FilterTrigger>
						<FilterDrawer isSinglePanelMode />
					</>
				) : (
					<ProductsFilterContainer
						scrollToListingHeader={scrollToListingHeader}
						renderComponent={props => (
							<ProductsFilter
								{...props}
								className="listing__filters"
								filtersRef={ref}
								isStuck={isSticky}
								isSticky={true}
							/>
						)}
					/>
				)}

				<ListingContentContainer
					displayProductsCount={displayProductsCount}
					spotlightUri={idSpotlight}
				/>

				<PartnerSEO
					title={listingSEO.title}
					description={listingSEO.description}
					resolution={resolution}
				/>

				<ScrollToTopButton />

				<CookiePolicyFooterContainer />
			</div>
			<PushNotificationContainer />
		</FilterProvider>
	);
};

Listing.propTypes = {
	brand: brandPropTypes,
	idSpotlight: PropTypes.string,
	listingSEO: PropTypes.object,
	displayProductsCount: PropTypes.bool,
	resolution: PropTypes.string,
	resetAllSDPProductsFilter: PropTypes.func,
	products: PropTypes.arrayOf(PropTypes.object),
};

export default Listing;
